import { Button, Calendar, Card, Input, Repeat, Search } from "@app/ui";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildSearchIndex, searchIndex } from "../lib/search.js";
import { ShowMoreFooter, usePagedRows } from "../lib/paged-list.js";
import { useTimeFormat } from "../lib/useTimeFormat.js";
import { useI18n } from "../lib/useI18n.js";
import { useCalendarFocusStore, useSettingsStore, useTaskDialogStore, useTasksStore } from "../stores/index.js";

export function SearchRoute() {
  const { t: tr } = useI18n();
  const tasks = useTasksStore((s) => s.tasks);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const focusCalendar = useCalendarFocusStore((s) => s.focus);
  const lastCalendarView = useSettingsStore((s) => s.lastCalendarView);
  const navigate = useNavigate();
  const tf = useTimeFormat();

  function viewOnCalendar(t: (typeof tasks)[number]) {
    if (!t.due_at) return;
    focusCalendar(new Date(t.due_at), t.id);
    navigate(`/calendar/${lastCalendarView}`);
  }
  const [query, setQuery] = useState("");
  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);

  // Precompute the lowercased haystacks once per task list; filtering each
  // keystroke then only scans strings, not re-lowercasing every task's text.
  const index = useMemo(() => buildSearchIndex(tasks), [tasks]);
  const results = useMemo(() => {
    const matched = searchIndex(index, query);
    matched.sort((a, b) => a.title.localeCompare(b.title));
    return matched;
  }, [index, query]);

  // Matching is cheap (a substring scan over prebuilt haystacks); RENDERING is
  // not. A one-letter query matches most of a real dataset, and painting ten
  // thousand rows blocks the main thread for over a second on EVERY keystroke.
  const { shown, total, hasMore, showMore } = usePagedRows(results, query);

  const trimmed = query.trim();

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 px-1">
      <h1 className="text-2xl font-semibold">{tr("common.search")}</h1>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tr("search.placeholder")}
          className="pl-9"
        />
      </div>

      {trimmed === "" ? (
        <p className="text-sm text-muted-foreground">{tr("search.hint")}</p>
      ) : results.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tr("search.noMatch", { query: trimmed })}</p>
      ) : (
        <Card className="p-0 overflow-hidden">
          <ul className="flex flex-col divide-y divide-border text-sm">
            {shown.map((t) => {
              const done = t.status === "done";
              return (
                <li
                  key={t.id}
                  onClick={() => openEdit(t)}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent/30"
                  title={tr("agenda.clickToEdit")}
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`truncate ${done ? "text-muted-foreground line-through" : ""}`}>
                        {t.title || <em className="text-muted-foreground">{tr("agenda.untitled")}</em>}
                      </span>
                      {t.recurrence_id && <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={tr("agenda.recurring")} />}
                      {t.due_at && <span className="ml-auto shrink-0 text-xs text-muted-foreground">{tf.stamp(t.due_at)}</span>}
                    </div>
                    {(t.notes || t.location) && (
                      <span className="truncate text-xs text-muted-foreground">
                        {[t.location, t.notes].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                  {t.due_at && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={(e) => { e.stopPropagation(); viewOnCalendar(t); }}
                      title={tr("agenda.showOnCalendar")}
                    >
                      <Calendar className="h-4 w-4" /> {tr("search.view")}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {hasMore && <ShowMoreFooter shown={shown.length} total={total} onShowMore={showMore} />}
        </Card>
      )}
    </div>
  );
}
